import _ from "lodash";
import React from "react";
import { useAuth } from "react-oidc-context";
import { ClientReportType } from "../../../../types/ClientReportType";
import { ClientReportStatusType } from "../../../../types/ClientReportStatusType";
import { ClientReportStatusRecipients  } from "../types";
import { DefReactSelectOption } from "../../../../types/DefReactSelectOption";
import { PagedResults } from "../../../../types/PagedResults";
import { UserType } from "../../../../types/UserType";
import { useLazyFetchReportStatusesListQuery } from "../../../../services/clientreports";
import { useLazyFetchUsersListQuery } from "../../../../services/identity";
import { useLazyGetThreadByReportIdQuery } from "../../../../services/messages/reportMessagesApiEndpoints";
import { toNString } from "../../../../helpers/guids";

/*
Provider that basically checks if a user can do X or Y with a report
*/

const ReportControlContext = React.createContext<{
    availableUsers: DefReactSelectOption<UserType>[],
    currentStatus: ClientReportStatusRecipients | null,
    isAllowedToAssignAuthor: boolean,
    isAllowedToSetDeadlineDate: boolean,
    isAllowedToSetActualMinutes: boolean,
    isAllowedToSetReportType: boolean,
    isLoading: boolean,
    isReportEditable: boolean,
    report: ClientReportType | null,
    reportStatuses: DefReactSelectOption<ClientReportStatusRecipients>[],
    reportThreadId: string | null
}>({ 
    availableUsers: [],
    currentStatus: null,
    isAllowedToAssignAuthor: false,
    isAllowedToSetActualMinutes: false,
    isAllowedToSetDeadlineDate: false,
    isAllowedToSetReportType: false,
    isLoading: false,
    isReportEditable: false,
    report: null,
    reportStatuses: [],
    reportThreadId: null
});

export const useReportControls = () => React.useContext(ReportControlContext);

export const ReportControlProvider = ({
    allowAssignAuthorRoles = ["sys_admin_access", "report_assign_author", "report_assign_self"],
    allowSetDeadlineDateRoles = ["sys_admin_access", "report_adjust_deadline"],
    allowAdjustMinutesRoles = ["sys_admin_access", "report_adjust_minutes"],
    allowAdjustTypeRoles = ["sys_admin_access", "report_adjust_type"],
    children,
    data = null
} : Readonly<{ 
    allowAssignAuthorRoles?: string[],
    allowSetDeadlineDateRoles?: string[],
    allowAdjustMinutesRoles?: string[],
    allowAdjustTypeRoles?: string[],
    children: React.ReactNode,
    data: ClientReportType | null
}>) => {
    const { user } = useAuth();
    const roles : string[] = user?.profile?.roles as string[] ?? [];
    const isAllowedToAssignAuthor = _.some(roles, (item : string) => allowAssignAuthorRoles.includes(item));
    const isAllowedToSetDeadlineDate = _.some(roles, (item : string) => allowSetDeadlineDateRoles.includes(item));
    const isAllowedToSetActualMinutes = _.some(roles, (item : string) => allowAdjustMinutesRoles.includes(item));
    const isAllowedToSetReportType = _.some(roles, (item : string) => allowAdjustTypeRoles.includes(item));
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [report, setReport] = React.useState<ClientReportType | null>(null);
    const [statuses, setStatuses] = React.useState<DefReactSelectOption<ClientReportStatusRecipients>[]>([]);
    const [reportThreadId, setReportThreadId] = React.useState<string | null>(null);
    const [availableUsers, setAvailableUsers] = React.useState<DefReactSelectOption<UserType>[]>([]);
    const [fetchStatuses] = useLazyFetchReportStatusesListQuery();
    const [fetchReportThread] = useLazyGetThreadByReportIdQuery();
    const [fetchUsers] = useLazyFetchUsersListQuery();

    const currentStatus = React.useMemo(() => statuses.find(el => el.value === data?.statusId)?.data ?? null, [ data, statuses ]);
    const isReportEditable = data?.isStatusCompliant === false && data?.isStatusTerminated === false;

    const buildReportStatusCollection = (statuses: DefReactSelectOption<ClientReportStatusType>[], users: DefReactSelectOption<UserType>[]) : DefReactSelectOption<ClientReportStatusRecipients>[] => {
        if (!report) {
            return [];
        }

        return statuses.map(status => {
            const { adviserId } = report;
            const { isAdviserNotified, rolesNotified } = status.data!;
            const userId : string = toNString(user?.profile.sub);

            let defaultRecipients = _.uniq(users.filter(user => {
                if (user.value === userId) {
                    return false;
                }
                if (isAdviserNotified === true && user.value === adviserId) {
                    return true;
                }
                if (rolesNotified.length > 0 && _.some(user.data!.roles, (i : string) => rolesNotified.includes(i))) {
                    return true;
                }
                return false;
            }).map(user => user.value));

            return {
                ...status,
                data: {
                    ...status.data, 
                    defaultRecipients
                }
            } as DefReactSelectOption<ClientReportStatusRecipients>;
        });
    };

    React.useEffect(() => {
        setReport(() => data);
    }, [ data ]);

    React.useEffect(() => {
        if (!report) {
            return;
        }

        // turn on loading so that we can block some UI controls
        setIsLoading(() => true);

        // setup our promise collection
        const statusesFetch = fetchStatuses({ masterAccountId: report.masterAccountId, reportId: report.id }, true).unwrap();
        const reportThreadIdFetch = fetchReportThread({ reportId: report.id }).unwrap();
        const usersFetch = fetchUsers({ includeRoles: true }).unwrap();

        // wait for all the promises to be completed, then we can handle each one in turn.
        Promise.allSettled([ statusesFetch, reportThreadIdFetch, usersFetch ])
            .then(
                (r) => {
                    setReportThreadId(() => (r[1] as PromiseFulfilledResult<string>).value);
                    setAvailableUsers(() => (r[2] as PromiseFulfilledResult<PagedResults<DefReactSelectOption<UserType>>>).value.results);
                    setStatuses(() => buildReportStatusCollection(
                        (r[0] as PromiseFulfilledResult<DefReactSelectOption<ClientReportStatusType>[]>).value, 
                        (r[2] as PromiseFulfilledResult<PagedResults<DefReactSelectOption<UserType>>>).value.results
                    ));
                }
            )
            .finally(() => {
                setIsLoading(() => false);
            });
    }, [ report ]);

    return (
        <ReportControlContext.Provider value={{ 
            availableUsers,
            currentStatus,
            isAllowedToAssignAuthor,
            isAllowedToSetDeadlineDate,
            isAllowedToSetActualMinutes,
            isAllowedToSetReportType,
            isLoading,
            isReportEditable,
            report,
            reportStatuses: statuses,
            reportThreadId
        }}>
            {children}
        </ReportControlContext.Provider>
    );
}